import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { portalApi } from "../api/portalApi";
import { setMultipleJobDetails } from "./jobDetailsSlice"; // Import actions
import { updateHasValues } from "./hasValuesSlice"; // Import updateHasValues action
import { updateApiStatus } from "./apiStatusSlice"; // Import updateApiStatus action

// Async thunk to fetch both table IDs and job details in a single pass
export const fetchJobDetails = createAsyncThunk(
  "fetchJobDetails/fetchData",
  async ({ jobId, apiUrls }, { rejectWithValue, dispatch, getState }) => {
    try {
      const tableIds = {}; // Store table IDs with zone labels for each table
      const apiStatus = {}; // Object to keep track of API call statuses

      // Ensure apiUrls is an object
      if (typeof apiUrls !== 'object' || apiUrls === null) {
        throw new Error("Invalid apiUrls parameter");
      }

      // Loop through the provided API URLs (apiUrls is an object with table names as keys)
      for (const [tableName, apiUrl] of Object.entries(apiUrls)) {
        apiStatus[tableName] = { status: 'pending' }; // Set initial status to pending
        dispatch(updateApiStatus({ tableName, status: 'pending' })); // Dispatch pending status

        const isGeneralProject = tableName === "generalProject";

        try {
          let result;
          if (isGeneralProject) {
            result = await dispatch(
              portalApi.endpoints.getGeneralProjectInfo.initiate(jobId, { forceRefetch: true })
            );
          } else {
            // Strip the configured base URL prefix to get a relative path for the
            // generic getData endpoint (which prepends the base URL itself).
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const relativePath = apiUrl.startsWith(baseUrl)
              ? apiUrl.slice(baseUrl.length)
              : apiUrl;
            const requestUrl = `${relativePath}?projectID=${jobId}`;
            result = await dispatch(
              portalApi.endpoints.getData.initiate(
                { url: requestUrl, params: {} },
                { forceRefetch: true }
              )
            );
          }

          const data = result.data;
          const matchingItems = Array.isArray(data) ? data : [data];

          // Store the IDs with zone labels under the corresponding table name
          tableIds[tableName] = matchingItems.map((item) => ({
            zone: item.Zone,
            id: item.ID,
          }));

          // Dispatch the fetched job details to the store
          dispatch(setMultipleJobDetails({ section: tableName, updates: matchingItems }));
          dispatch(updateHasValues(matchingItems));

          apiStatus[tableName] = { status: 'success' };
          dispatch(updateApiStatus({ tableName, status: 'success' }));

        } catch (error) {
          if (error.response && error.response.status === 304) {
            apiStatus[tableName] = { status: 'not modified' };
            dispatch(updateApiStatus({ tableName, status: 'not modified' }));
            continue;
          }

          const status = error.response?.status || 'unknown';
          apiStatus[tableName] = { status: 'failed', error: status };
          dispatch(updateApiStatus({ tableName, status: 'failed', error: status }));
          throw new Error(`Failed to fetch data for ${tableName}: ${status}`);
        }
      }
      return tableIds;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch table IDs and job details");
    }
  }
);

// Define the slice to manage fetch table IDs and job details state
const fetchJobDetailsSlice = createSlice({
  name: "fetchJobDetails",
  initialState: {
    loading: false, // Tracks the loading state
    error: null, // Stores any errors
  },
  reducers: {
    // We can add more reducers here if needed
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch table IDs and job details";
      });
  },
});

// Selector to get the loading and error state
export const selectFetchJobDetailsLoading = (state) => state.fetchJobDetails.loading;
export const selectFetchJobDetailsError = (state) => state.fetchJobDetails.error;

// Export the reducer for the fetch table IDs and job details slice
const fetchJobDetailsReducer = fetchJobDetailsSlice.reducer;
export default fetchJobDetailsReducer;
